import { TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import * as SFX from "Data/SoundEffect";
import * as VO from "Data/VoiceOver";
import * as Topics from "Data/Topics";
import Settings from "Data/Settings";
import { RestStopCompletePopupLayout } from "Layouts/RestStopCompletePopupLayout";
import { LayoutSprite } from "GameObjects/UI/LayoutSprite";
import { LayoutButton } from "GameObjects/UI/LayoutButton";
import { Text } from "GameObjects/UI/Text";
import { IRuntimeDuck } from "Data/GameState";
import { LayoutNineSliceSprite } from "GameObjects/UI/LayoutNineSliceSprite";
import { templateResources } from "Data/templateResources";
import { ResourceType } from "Data/ResourceType";

export default class RestStopCompletePopup extends HLF.Popup implements ReturnType<typeof RestStopCompletePopupLayout> {
    public static readonly NAME = "RestStopCompletePopup";

    public readonly bg: LayoutSprite;
    public readonly energyBarEmpty: LayoutSprite;
    public readonly energyBarSegment: LayoutSprite[];
    public readonly templateDuck: LayoutSprite;
    public readonly duckNameBg: LayoutNineSliceSprite;
    public readonly batteryIcon: LayoutSprite;
    public readonly placeholderDuck: LayoutSprite[];
    public readonly continueButton: LayoutButton;
    protected readonly bodyText: PIXI.BitmapText;
    private readonly _timelines: TimelineLite[] = [];

    public constructor() {
        super();
        this.bg = new LayoutSprite();
        this.continueButton = new LayoutButton(this.onContinueButtonClicked.bind(this), VO.REST_STOP_DONE, SFX.UI_CLICK);
        this.bodyText = HLF.PixiUtils.makeText("Your ducks have rested.", Constants.FONT_RIFFICFREE_BOLD_36, 36, 0xffffff);
        this.energyBarEmpty = new LayoutSprite();
        this.energyBarSegment = [
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
        ];
        this.templateDuck = new LayoutSprite();
        this.duckNameBg = new LayoutNineSliceSprite();
        this.batteryIcon = new LayoutSprite();
        this.placeholderDuck = [
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
        ]
    }

    public init(pSize: PIXI.Point) {
        super.init(pSize);
        RestStopCompletePopupLayout.call(this);
        this.addChild(this.bg);
        this.addChild(this.bodyText);
        this.addChild(this.continueButton);
        const titleText = this.addChild(new Text("ENERGIZED!", Constants.FONT_RIFFICFREE_BOLD_36, 56, new PIXI.Point(0.5, 0.5)));
        titleText.position.x = 512;
        titleText.position.y = 384 - this.bg.height / 2;

        this.bodyText.position.x = 512;
        this.bodyText.position.y = 200;
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set(-1024 / 2, -768 / 2);
    }

    public show(pToken: HLF.IPopupToken) {
        super.show(pToken);
        console.log(pToken.data);
        if (pToken.data.firstTime === true) {
            this.continueButton.pulse();
        }

        this.bodyText.text = (pToken.data.pondName! + " has " + pToken.data.resourceCount + " food items").toUpperCase();

        if (pToken.data.ducks) {
            const ducks: (IRuntimeDuck & {previousEnergy: number})[] = pToken.data.ducks;
            // copy asset and preserve relative position to duck
            const cloneSprite = (sprite: PIXI.Sprite) => {
                const clone = new PIXI.Sprite(sprite.texture);
                clone.anchor.copyFrom(sprite.anchor);
                clone.scale.copyFrom(sprite.scale);
                clone.position = HLF.PointUtils.subtract(sprite.position, this.templateDuck.position);
                return clone;
            }
            // copy asset and preserve relative position to duck
            const cloneNineSliceSprite = (sprite: LayoutNineSliceSprite) => {
                const clone = new LayoutNineSliceSprite(sprite.leftWidth, sprite.topHeight, sprite.rightWidth, sprite.bottomHeight);
                clone.setLayoutTexture(sprite.name, sprite.anchorX, sprite.anchorY);
                clone.position = HLF.PointUtils.subtract(sprite.position, this.templateDuck.position);
                return clone;
            }
            const centerThisContainer = this.addChild(new PIXI.Container());
            ducks.forEach((duck, i) =>{


                const _duck = centerThisContainer.addChild(new PIXI.Container());
                const duckSprite = _duck.addChild(new LayoutSprite());
                duckSprite.setLayoutTexture(duck.config.swapSprite, 0.5, 0.5);
                if (i === 0) {
                    _duck.position.copyFrom(this.templateDuck.position);
                }
                else {
                    _duck.position.copyFrom(this.placeholderDuck[i-1].position);
                }
                const duckNameBg = cloneNineSliceSprite(this.duckNameBg);
                const duckNameText = new Text(duck.name, Constants.FONT_RIFFICFREE_BOLD_36, 24, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontScale: true});
                duckNameText.align = "center";
                const duckNameBgScale = Math.max((duckNameText.textWidth + 18) / duckNameBg.width, 1);
                duckNameBg.setNineSliceScale(duckNameBgScale, 1);
                duckNameBg.addChild(duckNameText);
                _duck.addChild(duckNameBg);
                duckNameBg.scale.set(Settings.textSizeMultiplier); // scale both text and bg
                const energyMeter = _duck.addChild(new PIXI.Container());

                const energyBarBg = cloneSprite(this.energyBarEmpty);
                energyMeter.addChild(energyBarBg);
                const duckEnergyOutOfFive = Math.floor(duck.energy / 20) || Math.round(duck.energy / 20); // if it's less than 1/5, round instead of floor
                const duckPreviousEnergyOutOfFive = Math.floor(duck.previousEnergy / 20) || Math.round(duck.previousEnergy / 20); // if it's less than 1/5, round instead of floor

                console.log(duck.name + " " + duckPreviousEnergyOutOfFive + " -> " + duckEnergyOutOfFive);

                for (let n = 0; n < duckEnergyOutOfFive; n++) {
                    const segment = cloneSprite(this.energyBarSegment[n]);
                    let text: PIXI.BitmapText;
                    switch (Settings.difficulty) {
                        default:
                        case 0 / 3: // adaptive difficulty
                            switch (ducks.length) {
                                case 5:
                                default:
                                    text = segment.addChild(new Text((n + 1).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, 24, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                                    break;
                                case 7:
                                    text = segment.addChild(new Text(((n + 1) * 5).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, 22, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                                    break;
                                case 9:
                                    text = segment.addChild(new Text(((n + 1) * 10).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, 22, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                                    break;
                            }
                        break;
                        case 1 / 3:
                            text = segment.addChild(new Text((n + 1).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, 24, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                            break;
                        case 2 / 3:
                            text = segment.addChild(new Text(((n + 1) * 5).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, 22, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                            break;
                        case 3 / 3:
                            text = segment.addChild(new Text(((n + 1) * 10).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, 22, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                            break;
                    }
                    text.y = 1;
                    energyMeter.addChild(segment);
                    energyMeter.scale.set(HLF.MathUtils.lerp(0.75, 1, Settings.textSizeMultiplier / 1.75)); // max text size multiplier is 1.75x

                    if (n > duckPreviousEnergyOutOfFive) {
                        TweenLite.from(segment, 0.25, {pixi: {scale: 0}}).delay((n * 0.5) + 1.5);
                    }
                }

                const batteryIcon = cloneSprite(this.batteryIcon);
                _duck.addChild(batteryIcon);
            });

            // hide template assets
            this.duckNameBg.visible = false;
            this.energyBarEmpty.visible = false;
            this.energyBarSegment.forEach(s=>s.visible = false);
            this.templateDuck.visible = false;
            this.batteryIcon.visible = false;

            if (ducks.length === 5) {
                centerThisContainer.position.x = -175;
            }
            else if (ducks.length === 7) {
                centerThisContainer.position.x = -75;
            }
        }

        const resourceContainer = this.addChild(new PIXI.Container());
        for (let i = 0; i < pToken.data.resources.length; i++) {
            const resourceSprite = resourceContainer.addChild(new LayoutSprite());
            resourceSprite.setLayoutTexture(templateResources[pToken.data.resources[i].type as ResourceType].sprite, 0.5, 0.5);
            resourceSprite.x = resourceContainer.width;
            const t = new TimelineLite();
            t.call(()=>{
                HLF.Application.instance.audio.play(SFX.ENERGY_REFILL, 1, false, HLF.AudioCategory.SFX);
            })
            .to(resourceSprite, 0.25, {pixi: {scale: 0}}).delay(((pToken.data.resources.length - i) * 0.5 + 2));
            this._timelines.push(t);
        }
        resourceContainer.x = 512 - (resourceContainer.width / 2);
        resourceContainer.y = 250;
    }

    protected AnimateIn(pCallback: () => void): void {
        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER);
        PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this.continueButton);
        TweenLite.fromTo(this, 0.5, {pixi: {alpha: 0}}, {pixi: {alpha: 1}}).eventCallback(Constants.TWEEN_ONCOMPLETE, pCallback);
    }

    protected AnimateOut(pCallback: () => void): void {
        this._timelines?.forEach(t=>t.kill());
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER);
        TweenLite.to(this, 0.5, {pixi: {alpha: 0}}).eventCallback(Constants.TWEEN_ONCOMPLETE, pCallback);
    }

    protected onContinueButtonClicked() {
        this.continueButton.stopPulse();
        this.hide();
    }
}